import React, { useMemo } from 'react'
// components
import { Badge, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { ExternalLink } from 'react-feather'

// definitions
import * as AppDefinitions from '../../../App.d'
import { DetailedStripePayout, HistoryView, EventRetryBody } from '../../../screens/subsync/subsync'
import { StripeEventTransactionError } from '../../../../../back-end/common/stripeEventTransactionError'
import { ChildItem, Listing, ListingColumn } from '../../ui/listing/listing'
import { StripePayout } from '../../../../../back-end/common/stripePayout'

// utilities
import { date, time, currency, properCase } from '../../../utilities/format'

// helper functions
import * as functions from './functions'

interface PayoutHistoryProps {
	appState: AppDefinitions.AppState
	stripeFeesBillsEnabled: boolean
	creditNotesEnabled: boolean
	historyView: HistoryView
	updateHistoryView: (historyView: HistoryView) => void
	retryFailedEvent: (body: EventRetryBody) => void
}

const PayoutHistory = (props: PayoutHistoryProps) => {
	const expandedPayouts = useMemo(
		() =>
			props.historyView.payouts.map((payout) => ({
				...payout,
				children:
					payout.events?.[0].transactions?.[0].errors?.map((error) => ({
						...error,
						getIDFunc: () => error.stripeEventTransactionErrorID!,
					})) ?? [],
			})),
		[props.historyView.payouts]
	)
	return (
		<Listing<StripePayout>
			name="Event"
			namePlural="Events"
			list={expandedPayouts}
			getIDFunc={(item) => item.stripePayoutID!}
			customChildRender={customChildRender}
			innerTableClassName="subsync-table"
			individualActions={(item) => functions.getPayoutOverflowMenuOptions(item, props)}
			columns={columns}
			defaultSort={{ column: 'createTs', order: 'DSC' }}
			isLoading={false}
			paginationControls="bottom"
		/>
	)
}

const customChildRender = (item: StripeEventTransactionError & ChildItem) => (
	<>
		<td></td>
		<td className="pl-5">Error:</td>
		<td colSpan={6}>{item.message}</td>
		<td>
			{item.stripeObjectType && item.stripeID && (
				<a href={`https://dashboard.stripe.com/${item.stripeObjectType}s/${item.stripeID}`} target="_blank" rel="noreferrer">
					<ExternalLink />
				</a>
			)}
		</td>
	</>
)

const columns = [
	{
		value: (item) => item.events![0]?.transactions?.[0]?.createTs,
		render: (item) => <>{item.events![0]?.transactions?.[0]?.createTs ? `${date(item.events![0].createTs!)} ${time(item.events![0].createTs!)}` : ''}</>,
		showHeader: true,
		headerText: 'Time',
		sortColumnName: 'createTs',
		filterType: 'dateRange',
		filterOptions: {
			columnName: 'createTs',
		},
	},
	{
		value: (item) => item.stripeID,
		render: (item) => <p>{item.stripeID}</p>,
		showHeader: true,
		headerText: 'Payout',
		sortColumnName: 'stripeID',
		filterType: 'string',
		filterOptions: {
			columnName: 'stripeID',
		},
	},
	{
		value: (item) => item.amount,
		render: (item) => {
			const latestTransaction = item.events![0]?.transactions?.[0]
			const amount = currency(item.amount, item.currency)
			return latestTransaction ? (
				latestTransaction.result !== 'success' ? (
					<OverlayTrigger
						placement={'left'}
						overlay={
							<Tooltip id={`${latestTransaction.stripeEventTransactionID}-tooltip`}>
								{latestTransaction.message || properCase(latestTransaction.result ?? '')}
							</Tooltip>
						}
					>
						<Badge variant={latestTransaction.result === 'error' ? 'danger' : 'warning'} className={'subsync-badge'}>
							{amount}
						</Badge>
					</OverlayTrigger>
				) : (
					<Badge variant={latestTransaction.result === 'success' ? 'success' : 'warning'} className={'subsync-badge'}>
						{amount}
					</Badge>
				)
			) : (
				<Badge variant={'secondary'} className={'subsync-badge'}>
					{amount}
				</Badge>
			)
		},
		showHeader: true,
		headerText: 'Amount',
		sortColumnName: 'amount',
		filterType: 'string',
		filterOptions: {
			columnName: 'amount',
		},
	},
	{
		value: (item) => item.stripeOrg?.stripeOrg?.name ?? '',
		render: (item) => <p>{item.stripeOrg?.stripeOrg?.name ?? ''}</p>,
		showHeader: true,
		headerText: 'Stripe',
		sortColumnName: 'stripeOrg.stripeOrg.name',
		filterType: 'string',
		filterOptions: {
			columnName: 'stripeOrg.stripeOrg.name',
		},
	},
	{
		value: (item) => item.xeroOrg?.xeroOrg?.name ?? '',
		render: (item) => <p>{item.xeroOrg?.xeroOrg?.name ?? ''}</p>,
		showHeader: true,
		headerText: 'Xero',
		sortColumnName: 'xeroOrg.xeroOrg.name',
		filterType: 'string',
		filterOptions: {
			columnName: 'xeroOrg.xeroOrg.name',
		},
	},
	{
		value: (item) => item.events![0]?.transactions?.[0]?.result ?? '',
		render: (item) => {
			const latestTransaction = item.events![0]?.transactions?.[0]
			return latestTransaction ? (
				latestTransaction.result !== 'success' ? (
					<OverlayTrigger
						placement={'left'}
						overlay={
							<Tooltip id={`${latestTransaction.stripeEventTransactionID}-tooltip`}>
								{latestTransaction.message || properCase(latestTransaction.result ?? '')}
							</Tooltip>
						}
					>
						<Badge
							variant={latestTransaction.result === 'error' ? 'danger' : 'warning'}
							className={'subsync-badge'}
							style={{ textTransform: 'capitalize' }}
						>
							{latestTransaction.result}
						</Badge>
					</OverlayTrigger>
				) : (
					<Badge
						variant={latestTransaction.result === 'success' ? 'success' : 'warning'}
						className={'subsync-badge'}
						style={{ textTransform: 'capitalize' }}
					>
						{latestTransaction.result}
					</Badge>
				)
			) : (
				<></>
			)
		},
		showHeader: true,
		headerText: 'Processing Status',
		sortColumnName: 'result',
		filterType: 'string',
		filterOptions: {
			columnName: 'result',
		},
	},
	{
		value: (item) => item.events!.at(-1)?.ts ?? '',
		render: (item) => {
			const originalEvent = item.events!.at(-1)
			return originalEvent ? `${date(originalEvent.ts!)} ${time(originalEvent.ts!)}` : ''
		},
		showHeader: true,
		headerText: 'Payout Date',
		sortColumnName: 'events.at(-1).ts',
		filterType: 'dateRange',
		filterOptions: {
			columnName: 'events.at(-1).ts',
		},
	},
] as ListingColumn<DetailedStripePayout>[]

export { PayoutHistory }
